import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import  LogoContainer  from "../../../../../shared/components/LogoContainer";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import { createInvestment } from "../../../../../../app/store/slices/investmentSlice";
import { toast } from "react-toastify";
import  AddAnotherList  from "../../../../../shared/components/AddAnotherList";
import InputForm from "../../../../../shared/components/InputForm";
import FormSelect from "../../../../../shared/components/FormSelect";
import { investmentTypes, tradeTypes, investmentTableFields} from '../../../../../../utils/constants/dashboardConstants'
import { currencyOptions } from '../../../../../../utils/constants/currencyOptions';

const initialInvestment = {
  assetName: "",
  ticker: "",
  type: "",
  trade: "",
  tradeDate: "",
  orderPrice: "",
  currency: "",
  units: "",
  cost: "",
  value: "",
  profitLoss: "",
  exchange: "",
  orderFees: "",
  paymentAccountId: "",
};

const InvestmentManualSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);
  const [selectedCanvasId] = useState(
    location.state?.canvasId ||
      JSON.parse(localStorage.getItem("canvasData") || "{}")?._id ||
      ""
  );
  const [selectedAccountId] = useState(location.state?.accountId || "");

  const [investments, setInvestments] = useState([]);
  const [currentInvestment, setCurrentInvestment] = useState({
    ...initialInvestment,
    paymentAccountId: selectedAccountId,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  // Fetch accounts for the selected canvas
  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId, limit:1000 }));
    }
  }, [dispatch, selectedCanvasId]);

  // Helper: get account object by id
  const getAccountById = (id) => accounts.find((acc) => acc._id === id);

  // Handlers
  const handleInputChange = (field, value) => {
    setCurrentInvestment((inv) => ({
      ...inv,
      [field]: value,
    }));
    setError("");
  };

  const handleAddInvestment = () => {
    setError("");
    for (const field of investmentTableFields) {
      if (!currentInvestment[field]) {
        setError("Please fill all required fields.");
        return;
      }
    }
    const account = getAccountById(currentInvestment.paymentAccountId);
    if (!account) {
      setError("Please select a valid account.");
      return;
    }
    setInvestments([...investments, currentInvestment]);
    setCurrentInvestment({ ...initialInvestment, paymentAccountId: selectedAccountId });
  };

  const handleContinue = async () => {
    setError("");
    setIsProcessing(true);
    try {
      let allInvestments = [...investments];
      
      const hasCurrent =
        investmentTableFields.every((f) => !!currentInvestment[f]) &&
        !investments.some(
          (inv) =>
            inv.assetName === currentInvestment.assetName &&
            inv.ticker === currentInvestment.ticker &&
            inv.tradeDate === currentInvestment.tradeDate
        );
      if (hasCurrent) {
        allInvestments = [...investments, currentInvestment];
      }
      for (const inv of allInvestments) {
        await dispatch(
          createInvestment({
            canvasId: selectedCanvasId,
            investmentData: {
              ...inv,
              orderPrice: Number(inv.orderPrice),
              units: Number(inv.units),
              cost: Number(inv.cost),
              value: inv.value ? Number(inv.value) : undefined,
              profitLoss: inv.profitLoss ? Number(inv.profitLoss) : undefined,
              orderFees: inv.orderFees ? Number(inv.orderFees) : undefined,
            },
          })
        ).unwrap();
      }
      navigate("/dashboard", {
        state: { canvasId: selectedCanvasId, accountId: selectedAccountId },
      });
    } catch (err) {
      toast.error(err);
      setError("Failed to create investment.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer />
      <div className="card-setup">
        <h2 className="txt-page-heading">Enter your Investments</h2>
        <p className="p-tag-below-heading text-center mb-8">
          Enter your investment details
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            {/* Asset Name */}
            <InputForm
              label="Asset Name"
              name="assetName"
              value={currentInvestment.assetName}
              onChange={(e) => handleInputChange("assetName", e.target.value)}
              placeholder="e.g. Apple Inc."
              required
            />
            {/* Ticker */}
            <InputForm
              label="Ticker"
              name="ticker"
              value={currentInvestment.ticker}
              onChange={(e) => handleInputChange("ticker", e.target.value)}
              placeholder="e.g. AAPL"
              required
            />
            {/* Investment Type */}
            <FormSelect
              label="Investment Type"
              name="type"
              value={currentInvestment.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              required
              options={[
                { value: "", label: "Select Type" },
                ...investmentTypes.map((type) => ({
                  value: type,
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                })),
              ]}
            />
            {/* Trade Type */}
            <FormSelect
              label="Trade Type"
              name="trade"
              value={currentInvestment.trade}
              onChange={(e) => handleInputChange("trade", e.target.value)}
              required
              options={[
                { value: "", label: "Select Trade" },
                ...tradeTypes.map((type) => ({
                  value: type,
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                })),
              ]}
            />
            {/* Trade Date */}
            <InputForm
              label="Trade Date"
              name="tradeDate"
              type="date"
              value={currentInvestment.tradeDate}
              onChange={(e) => handleInputChange("tradeDate", e.target.value)}
              required
            />
            {/* Order Price */}
            <InputForm
              label="Order Price"
              name="orderPrice"
              type="number"
              value={currentInvestment.orderPrice}
              onChange={(e) => handleInputChange("orderPrice", e.target.value)}
              min="0"
              step="0.000000000000001"
              required
            />
            {/* Currency */}
            <FormSelect
              label="Currency"
              name="currency"
              value={currentInvestment.currency}
              onChange={(e) => handleInputChange("currency", e.target.value)}
              required
              options={[
                { value: "", label: "Select Currency" },
                ...currencyOptions.map(cur => ({
                  value: cur.code,
                  label: `${cur.code} - ${cur.symbol} (${cur.name})`,
                })),
              ]}
            />
            {/* Units */}
            <InputForm
              label="Units"
              name="units"
              type="number"
              value={currentInvestment.units}
              onChange={(e) => handleInputChange("units", e.target.value)}
              min="0"
              step="0.00000000001"
              required
            />
            {/* Cost */}
            <InputForm
              label="Cost"
              name="cost"
              type="number"
              value={currentInvestment.cost}
              onChange={(e) => handleInputChange("cost", e.target.value)}
              min="0"
              step="0.000000000001"
              required
            />
            {/* Value */}
            <InputForm
              label="Value"
              name="value"
              type="number"
              value={currentInvestment.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              min="0"
              step="0.0000000001"
            />
            {/* Profit/Loss */}
            {/* <InputForm
              label="Profit/Loss"
              name="profitLoss"
              type="number"
              value={currentInvestment.profitLoss}
              onChange={(e) => handleInputChange("profitLoss", e.target.value)}
              step="0.01"
            /> */}
            {/* Exchange */}
            <InputForm
              label="Exchange"
              name="exchange"
              value={currentInvestment.exchange}
              onChange={(e) => handleInputChange("exchange", e.target.value)}
            />
            {/* Order Fees */}
            <InputForm
              label="Order Fees"
              name="orderFees"
              type="number"
              value={currentInvestment.orderFees}
              onChange={(e) => handleInputChange("orderFees", e.target.value)}
              min="0"
              step="0.0000001"
            />
            {/* Payment Account */}
            <FormSelect
              label="Payment Account"
              name="paymentAccountId"
              value={currentInvestment.paymentAccountId}
              onChange={(e) => handleInputChange("paymentAccountId", e.target.value)}
              required
              options={[
                { value: "", label: "Select Account" },
                ...accounts.map((acc) => ({
                  value: acc._id,
                  label: `${acc.name} (${acc.currency})`,
                })),
              ]}
            />
            {/* Error message */}
            {error && (
              <div className="text-red-600 text-sm font-semibold mt-2">{error}</div>
            )}
            {/* Add Another Investment Button */}
            <div className="flex flex-row justify-end gap-6 w-full max-w-lg">
              <button
                type="button"
                className="btn-continue"
                onClick={handleAddInvestment}
              >
                Add Another Investment
              </button>
            </div>
          </div>
        </div>
        <AddAnotherList
          title="Your Investments"
          items={investments}
          className="w-full max-w-lg mx-auto"
          renderItem={(inv) => (
            <>
              <div>
                <div className="font-medium">{inv.assetName} ({inv.ticker})</div>
                <div className="text-sm text-gray-500">{inv.type} - {inv.trade}</div>
              </div>
              <div className="text-sm text-gray-500">
                {inv.units} @ {inv.orderPrice} {inv.currency}
              </div>
            </>
          )}
        />
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
            type="button"
            disabled={isProcessing}
          >
            Back
          </button>
          <button
            className="btn-continue"
            onClick={handleContinue}
            type="button"
          >
            {isProcessing ? "Processing..." : "Save & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentManualSetup;